import {
    getConnectorRelatedPoiIndices,
    isCornerSingleSidePoi,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { buildSolveConnectorPlacementRequest } from 'app/modules/wallforming/components/setting-container/connector-placement/utils/buildSolveConnectorPlacementRequest';
import { buildSolveCornerPlacementRequest } from 'app/modules/wallforming/components/setting-container/corner-placement/utils/buildSolveCornerPlacementRequest';
import { buildSolvePanelPlacementRequest } from 'app/modules/wallforming/components/setting-container/panel-placement/utils/buildSolvePanelPlacementRequest';
import { EMPTY_GUID } from 'shared/constants';

import type { SolveConnectorPlacementModel, SolveCornerPlacementModel, SolvePanelPlacementModel } from 'api/models';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

export interface CopilotPlacementTarget {
    poiId: string;
    poiType: 'wall' | 'corner';
    relatedPoiIds?: string[];
    panelPlacement?: SolvePanelPlacementModel;
    cornerPlacement?: SolveCornerPlacementModel;
    connectorPlacement?: SolveConnectorPlacementModel;
}

export function buildCopilotPlacementTargets(
    state: PlacementState,
    sessionId: string,
    systemId: string,
    includeSolverInputs: boolean
): CopilotPlacementTarget[] {
    return state.pois.reduce<CopilotPlacementTarget[]>((targets, { poi }, poiIndex) => {
        if (poi.poiGuid === EMPTY_GUID) {
            return targets;
        }

        if (isPlanarFacePoi(poi)) {
            const target: CopilotPlacementTarget = {
                poiId: poi.poiGuid,
                relatedPoiIds: includeSolverInputs
                    ? getConnectorRelatedPoiIndices(state.pois, poiIndex)
                          .map((index) => state.pois[index]?.poi.poiGuid)
                          .filter((id): id is string => id !== undefined && id !== EMPTY_GUID)
                    : undefined,
                poiType: 'wall'
            };
            if (includeSolverInputs) {
                target.panelPlacement = buildSolvePanelPlacementRequest({ state, sessionId, systemId, poiIndex });
                target.connectorPlacement = buildSolveConnectorPlacementRequest({
                    state,
                    sessionId,
                    systemId,
                    poiIndex
                });
            }
            targets.push(target);
            return targets;
        }

        if (isCornerSingleSidePoi(poi)) {
            const target: CopilotPlacementTarget = {
                poiId: poi.poiGuid,
                relatedPoiIds: includeSolverInputs
                    ? getConnectorRelatedPoiIndices(state.pois, poiIndex)
                          .map((index) => state.pois[index]?.poi.poiGuid)
                          .filter((id): id is string => id !== undefined && id !== EMPTY_GUID)
                    : undefined,
                poiType: 'corner'
            };
            if (includeSolverInputs) {
                target.cornerPlacement = buildSolveCornerPlacementRequest({ state, sessionId, systemId, poiIndex });
                target.connectorPlacement = buildSolveConnectorPlacementRequest({
                    state,
                    sessionId,
                    systemId,
                    poiIndex
                });
            }
            targets.push(target);
        }

        return targets;
    }, []);
}
